"use client";

import React from "react";
import { Server, Cpu, Sliders } from "lucide-react";

export interface AtrDataPipelineBlockProps {
    id?: string;
    tag?: string;
    title?: string;
    description?: string;
    steps?: {
        num: string;
        title: string;
        desc: string;
        icon: "Server" | "Cpu" | "Sliders";
        iconColor: string;
        footer: string;
    }[];
}

export default function AtrDataPipelineBlock({
    id = "telemetry-architecture",
    tag = "// System Data Pipeline",
    title = "FDR Ingestion & Kinematic Processing",
    description = "Our architecture processes recorded flight data parameters, mapping parsed timeseries logs directly into real-time physical behaviors and cockpit state variables.",
    steps = [
        {
            num: "01",
            title: "FDR Telemetry Ingestion",
            desc: "Ingests decoded flight parameter spreadsheets and CSV timeseries logs containing critical telemetry parameters. The pipeline aligns recorded timestamp samples with the simulator time steps.",
            icon: "Server",
            iconColor: "#00FF66",
            footer: "Parameter Ingestion • Sample Sync"
        },
        {
            num: "02",
            title: "Kinematic Calibration",
            desc: "Converts raw voltage values and digital offsets into standard aerodynamic measurements. The pipeline smooths sensor anomalies and aligns pitch, roll, and lateral acceleration indices.",
            icon: "Cpu",
            iconColor: "#00E5FF",
            footer: "Physics Solver • Drift Mitigation"
        },
        {
            num: "03",
            title: "Unity Digital Twin Render",
            desc: "Feeds synchronized streams into Unity. Controls 3D cockpit instruments, indicator lights, landing gear deployments, flaps, and all virtual control surface rotations instantly.",
            icon: "Sliders",
            iconColor: "#FFB800",
            footer: "3D Mapping • Multi-Display Sync"
        }
    ]
}: AtrDataPipelineBlockProps) {
    const renderIcon = (icon: string, color: string) => {
        const props = { className: "w-5 h-5", style: { color } };
        switch (icon) {
            case "Server": return <Server {...props} />;
            case "Cpu": return <Cpu {...props} />;
            case "Sliders": return <Sliders {...props} />;
            default: return <Server {...props} />;
        }
    };

    return (
        <section id={id} className="py-24 px-6 md:px-12 lg:px-24 bg-[#0C0D0E] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="max-w-3xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono uppercase tracking-widest text-[#00FF66] block">
                            {tag}
                        </span>
                    )}
                    <h2 className="font-cormorant font-serif text-3xl md:text-5xl font-light text-white mt-4 mb-6">
                        {title}
                    </h2>
                    {description && (
                        <p className="text-sm md:text-base text-white/50 leading-relaxed font-light">
                            {description}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {steps.map((step, idx) => (
                        <div 
                            key={idx} 
                            className="bg-[#111214] border border-white/[0.05] p-8 rounded-xl flex flex-col justify-between space-y-6"
                        >
                            <div className="space-y-4">
                                <div className="w-10 h-10 rounded-lg bg-white/[0.02] border border-white/10 flex items-center justify-center">
                                    {renderIcon(step.icon, step.iconColor)}
                                </div>
                                <h3 className="text-lg font-medium text-white">
                                    {step.num} / {step.title}
                                </h3>
                                <p className="text-xs text-white/50 leading-relaxed font-light">
                                    {step.desc}
                                </p>
                            </div>
                            <div className="text-[10px] font-mono text-white/30 uppercase tracking-widest pt-4 border-t border-white/[0.05]">
                                {step.footer}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
